import { Plus, Trash01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";

export type ProductInfoData = {
  productName: string;
  productDescription: string;
};

/** A single placeholder/value row. We use an array (not a plain object) so users
 * can edit the key without losing focus or row position while typing. */
export type DomainKnowledgeRow = {
  key: string;
  value: string;
};

interface DomainKnowledgeProps {
  rows: DomainKnowledgeRow[];
  onChange: (rows: DomainKnowledgeRow[]) => void;
  productInfo: ProductInfoData;
  onProductInfoChange: (data: ProductInfoData) => void;
}

const PLACEHOLDER_KEY_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

// Domain-knowledge keys every merchant should always have. They render as fixed
// rows (key locked, no delete) above the free-form rows, but persist inside the
// domainKnowledge record like any other row — a row with one of these keys is
// routed to its fixed row instead of the free-form list.
const DEFAULT_VARIABLE_ROWS = [
  { key: "brand_name", label: "Brand name", placeholder: "e.g. Acme" },
  { key: "own_brands", label: "Own brands", placeholder: "Brands the merchant owns or is associated with" },
  {
    key: "licensed_collabs",
    label: "Licensed collabs",
    placeholder: "Collaborating brands that should not be flagged",
  },
];

const DEFAULT_VARIABLE_KEYS = new Set(DEFAULT_VARIABLE_ROWS.map((r) => r.key));

/** Convert array rows → Record for sending in the API payload.
 * Drops empty keys AND empty values (so blank default-suggestion rows don't get
 * persisted), and de-duplicates by last-write-wins. */
export const rowsToRecord = (rows: DomainKnowledgeRow[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const { key, value } of rows) {
    const trimmedKey = key.trim();
    if (trimmedKey && value !== "") out[trimmedKey] = value;
  }
  return out;
};

/** Convert Record (from server) → array rows for editing. */
export const recordToRows = (record: Record<string, string> | undefined): DomainKnowledgeRow[] => {
  if (!record) return [];
  return Object.entries(record).map(([key, value]) => ({ key, value }));
};

export const DomainKnowledge = ({ rows, onChange, productInfo, onProductInfoChange }: DomainKnowledgeProps) => {
  const updateRow = (idx: number, patch: Partial<DomainKnowledgeRow>) => {
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx: number) => {
    onChange(rows.filter((_, i) => i !== idx));
  };

  const addRow = () => {
    onChange([...rows, { key: "", value: "" }]);
  };

  // Last row holding a default key (rowsToRecord is last-write-wins), or -1.
  const lastIndexOfKey = (key: string) => rows.reduce((acc, r, i) => (r.key.trim() === key ? i : acc), -1);

  const setDefaultVariable = (key: string, value: string) => {
    const idx = lastIndexOfKey(key);
    if (idx === -1) onChange([...rows, { key, value }]);
    else updateRow(idx, { value });
  };

  const freeRows = rows
    .map((row, idx) => ({ row, idx }))
    .filter(({ row }) => !DEFAULT_VARIABLE_KEYS.has(row.key.trim()));

  // Product name/description persist as dedicated config fields, not in the
  // domainKnowledge record — they render as fixed rows here because they fill
  // their {placeholders} the same way the custom rows fill theirs.
  const builtinRows = [
    {
      key: "product_name",
      label: "Product name",
      placeholder: "e.g. Brand Energy Drink",
      value: productInfo.productName,
      set: (value: string) => onProductInfoChange({ ...productInfo, productName: value }),
    },
    {
      key: "product_description",
      label: "Product description",
      placeholder: "A brief description of the product...",
      value: productInfo.productDescription,
      set: (value: string) => onProductInfoChange({ ...productInfo, productDescription: value }),
    },
  ];

  return (
    <div className="space-y-4 pt-14 pb-12">
      <div>
        <h2 className="text-lg font-semibold text-primary">Merchant variables</h2>
        <p className="text-sm text-tertiary">
          Values defined here are specific to this merchant and get substituted into any detection prompt above wherever
          the matching <code>{"{variable}"}</code> appears — e.g. <code>{"{product_name}"}</code>. Saving fails if an
          enabled risk's prompt uses a variable with no value here.
        </p>
      </div>

      <div className="space-y-2">
        {builtinRows.map(({ key, label, placeholder, value, set }) => (
          <div key={key} className="rounded-lg bg-secondary p-3 grid grid-cols-12 gap-3 items-start">
            <div className="col-span-4">
              <Input aria-label={`Default variable ${key}`} value={key} isDisabled />
            </div>
            <div className="col-span-7">
              <Input aria-label={label} placeholder={placeholder} value={value} onChange={set} />
            </div>
          </div>
        ))}

        {DEFAULT_VARIABLE_ROWS.map(({ key, label, placeholder }) => (
          <div key={key} className="rounded-lg bg-secondary p-3 grid grid-cols-12 gap-3 items-start">
            <div className="col-span-4">
              <Input aria-label={`Default variable ${key}`} value={key} isDisabled />
            </div>
            <div className="col-span-7">
              <Input
                aria-label={label}
                placeholder={placeholder}
                value={lastIndexOfKey(key) === -1 ? "" : rows[lastIndexOfKey(key)].value}
                onChange={(value: string) => setDefaultVariable(key, value)}
              />
            </div>
          </div>
        ))}

        {freeRows.map(({ row, idx }) => {
          const trimmedKey = row.key.trim();
          const keyInvalid = trimmedKey !== "" && !PLACEHOLDER_KEY_PATTERN.test(trimmedKey);
          const duplicateKey = trimmedKey !== "" && rows.findIndex((r) => r.key.trim() === trimmedKey) !== idx;
          return (
            <div key={idx} className="rounded-lg bg-secondary p-3 grid grid-cols-12 gap-3 items-start">
              <div className="col-span-4">
                <Input
                  aria-label="Variable name"
                  placeholder="e.g. product"
                  value={row.key}
                  onChange={(value: string) => updateRow(idx, { key: value })}
                  hint={
                    keyInvalid
                      ? "Names can only have letters, numbers, and underscores — and can't start with a number."
                      : duplicateKey
                        ? "This name is already used above — the last one will overwrite the others when saved."
                        : undefined
                  }
                  isInvalid={keyInvalid || duplicateKey}
                />
              </div>
              <div className="col-span-7">
                <Input aria-label="Value" value={row.value} onChange={(value: string) => updateRow(idx, { value })} />
              </div>
              <div className="col-span-1 flex justify-end pt-1">
                <Button
                  size="sm"
                  color="tertiary"
                  iconLeading={Trash01}
                  onClick={() => removeRow(idx)}
                  aria-label={`Remove variable ${row.key || `row ${idx + 1}`}`}
                >
                  {""}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Button size="sm" color="secondary" iconLeading={Plus} onClick={addRow}>
        Add variable
      </Button>
    </div>
  );
};
